import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {LocationCardScratchpadState} from '../../scratchpad/locationCardScratchpad.types';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import getOrCreateLocationCard from '../../utils/location/getOrCreateLocationCard';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import askAboutObjectsTool from '../../../../tools/general/askAboutObjectsTool/askAboutObjectsTool';
import setLocationFeedTool from './setLocationFeedTool';
import setLocationSetTool from './setLocationSetTool';
import setLocationTargetingMethodTool from './setLocationTargetingMethodTool';
import setMaxDistanceTool from './setMaxDistanceTool';
import setTopLocationsTool from './setTopLocationsTool';

const cardSpecificInstructions = `- Use the ${V2CardAgentToolName.SearchLocationFeeds} tool to search for location feeds when the user doesn't provide a specific feed ID.
- Use the ${V2CardAgentToolName.SearchLocationSets} tool to search for location sets when the user doesn't provide a specific set ID.
- When setting a location set, you MUST also set the feed ID to match the location set's feed ID using the ${V2CardAgentToolName.SetLocationFeed} tool.
- Use the ${V2CardAgentToolName.SetLocationTargetingMethod} tool to set the targeting method based on user requirements (Nearest Location, By Zip Code, By DMA, Specific Store).
- For proximity-based targeting (Nearest Location, By Zip Code), use the ${V2CardAgentToolName.SetMaxDistance} tool to configure distance and fallback options.
- For Specific Store targeting, use the ${V2CardAgentToolName.SetTopLocations} tool to set the specific location IDs.`;

export default function addOrUpdateLocationCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, LocationCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId: cardId} = preProcessStandaloneAgent(params, store);

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateLocationCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.STORE]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.STORE,
        cardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getCardDetailsTool(),
        askAboutObjectsTool(store),

        // Location tools
        setLocationFeedTool(),
        setLocationSetTool(),
        setLocationTargetingMethodTool(),
        setMaxDistanceTool(),
        setTopLocationsTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card: await getOrCreateLocationCard(cardId, store)};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateLocationCard,
    displayName: 'Working on location card',
    description:
      'Add or update a location card. The card selects a single location from location feeds to inject into ad context for creative display or decisioning. ' +
      'Can configure location selection strategy (targeting method), assign a feed and location set, ' +
      'configure max distance and fallback options for proximity-based targeting, or assign specific store location IDs for specific store targeting.',
    execute,
    inputSchema: addOrUpdateCardInputSchema,
  });
}
